import React from 'react'
import { useIntl } from 'react-intl'

import AddressWithViewMapButton from './Common/AddressWithViewMapButton'
import ConfirmationDetails from './Common/ConfirmationDetails'
import Divider from './Common/Divider'
import ItemTypeAndBookedLabel from './Common/ItemTypeAndBookedLabel'
import SummaryAdditionalNotes from './Common/SummaryAdditionalNotes'
import SummaryDataItem from './Common/SummaryDataItem'
import SummaryDateTime from './Common/SummaryDateTime'
import SummaryImage from './Common/SummaryImage'
import DefaultSummaryModalFooter from './Common/SummaryModalFooter/DefaultSummaryModalFooter'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import GooglePlaceRating from 'tripPlanner/components/GooglePlaceRating/GooglePlaceRating'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import type { AttractionItem } from 'tripPlanner/types/tripItem'
import { maybePlaceId } from 'tripPlanner/utils'
import GoogleEditorialSummary from 'tripPlanner/components/GoogleEditorialSummary'

interface Props {
  item: AttractionItem
  editorialSummary?: string
}

function AttractionItemSummary(props: Props) {
  const { item, editorialSummary } = props
  const intl = useIntl()

  const { closeModal, goBack } = useModalCallbacks()
  const placeId = maybePlaceId(item.place)

  const reviewsLabel = intl.formatMessage({
    id: 'at4hARsNyS',
    defaultMessage: 'Reviews',
    description: 'Label for the Google reviews section in attraction trip item summary',
  })
  const websiteLabel = intl.formatMessage({
    id: 'QYJ1hWnKr0',
    defaultMessage: 'Website',
    description: 'Label for the website link in attraction trip item summary',
  })
  const phoneNumberLabel = intl.formatMessage({
    id: 'UTkfLLclBz',
    defaultMessage: 'Phone number',
    description: 'Label for the phone number in attraction trip item summary',
  })

  return (
    <>
      <ModalHeader
        title={item.place.name}
        subtitle={item.place.region}
        onCloseButtonClick={closeModal}
        onBackButtonClick={goBack}
      />

      <ModalBody>
        <SummaryImage
          place={item.place}
          tripId={item.tripId}
          tripItemId={item.id}
        />
        <ModalContent>
          <VerticalSpacer gap={20}>
            <ItemTypeAndBookedLabel item={item} />
            {(item.startDate || item.startDay) && (
              <SummaryDateTime
                date={item.startDate}
                time={item.startTime}
                day={item.startDay}
              />
            )}
            {item.place.address && (
              <AddressWithViewMapButton
                item={item}
                address={item.place.address}
              />
            )}
            {placeId && (
              <GooglePlaceRating size="M" placeId={placeId} label={reviewsLabel} />
            )}
            {editorialSummary && (
              <GoogleEditorialSummary editorialSummary={editorialSummary} variant="large" />
            )}
            <ConfirmationDetails
              isBooked={item.isBooked}
              confirmationCode={item.confirmationCode}
              price={item.price}
            />
            <Divider />
            <SummaryDataItem title={websiteLabel} value={item.url} url={item.url} />
            <SummaryDataItem
              title={phoneNumberLabel}
              value={item.phone}
              url={`tel:${item.phone}`}
            />
            <SummaryAdditionalNotes notes={item.notes} />
          </VerticalSpacer>
        </ModalContent>
      </ModalBody>

      <DefaultSummaryModalFooter item={item} />
    </>
  )
}

export default AttractionItemSummary
